import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { pingAPI, pingHelpers } from "@/lib/ping-api";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import type {
  CreateChatForm,
  SendMessageForm,
  UpdateChatStatusForm,
  SupportChat,
  SupportChatDetail,
  Message,
  PaginatedResponse,
} from "@/types";
import { useToast } from "@/hooks/use-toast";
import { usePingStore } from "@/stores/ping-store";

const getSupportChatListQueries = (queryClient: QueryClient) =>
  queryClient
    .getQueryCache()
    .findAll({
      queryKey: queryKeys.ping.chats.all(),
      exact: false,
    })
    .filter((query) => query.queryKey[2] === "list");

const updateSupportChatInCachedLists = (
  queryClient: QueryClient,
  chatId: string,
  updater: (chat: SupportChat) => SupportChat
) => {
  const listQueries = getSupportChatListQueries(queryClient);

  listQueries.forEach((query) => {
    queryClient.setQueryData(
      query.queryKey,
      (oldData: PaginatedResponse<SupportChat> | undefined) => {
        if (!oldData?.results?.some((chat) => chat.id === chatId)) {
          return oldData;
        }

        return {
          ...oldData,
          results: oldData.results.map((chat) =>
            chat.id === chatId ? updater(chat) : chat
          ),
        };
      }
    );
  });
};

// Chat mutations
export const useCreatePingChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateChatForm) => pingAPI.chats.create(data),
    onSuccess: (chat: SupportChat) => {
      // Invalidate chat lists to refetch with new chat
      queryClient.invalidateQueries(invalidateQueries.pingChats());

      // Add the chat data to cache directly
      queryClient.setQueryData(queryKeys.ping.chats.detail(chat.id), chat);
    },
    onError: (error) => {
      console.error("Failed to create chat:", error);
    },
  });
};

export const useUpdatePingChatStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      data,
    }: {
      chatId: string;
      data: UpdateChatStatusForm;
    }) => pingAPI.chats.updateStatus(chatId, data),
    onSuccess: (chat: SupportChat, variables) => {
      // Update specific chat in cache directly
      queryClient.setQueryData(
        queryKeys.ping.chats.detail(variables.chatId),
        chat
      );

      // Invalidate chat lists to reflect status change
      queryClient.invalidateQueries(invalidateQueries.pingChats());
    },
    onError: (error) => {
      console.error("Failed to update chat status:", error);
    },
  });
};

// Message mutations
export const useSendPingMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessageForm) => pingAPI.messages.create(data),
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.messages.list(variables.chat),
      });

      // Optimistically add message to cache
      const previousMessages = queryClient.getQueryData(
        queryKeys.ping.messages.list(variables.chat)
      );

      // Determine if current user is a patient or care manager
      const { useAuthStore } = await import("@/stores/authStore");
      const { isPatient } = useAuthStore.getState();
      const isCurrentUserPatient = isPatient();

      // Create optimistic message
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}`,
        chat: variables.chat,
        user_sender: null, // Will be filled by server
        care_manager_sender: null,
        message_type: variables.message_type,
        text_content: variables.text_content || null,
        media_file: null,
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_user: false,
        is_read_by_care_manager: false,
        is_from_user: isCurrentUserPatient, // True for patients, false for care managers
        is_note: variables.is_note || false,
        sender_name: "You",
        user_type: "sending...",
      };

      // Optimistically update cache
      queryClient.setQueryData(
        queryKeys.ping.messages.list(variables.chat),
        (old: PaginatedResponse<Message> | undefined) => {
          if (!old)
            return {
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            };
          return {
            ...old,
            results: [...old.results, optimisticMessage],
          };
        }
      );

      return { previousMessages };
    },
    onSuccess: (response, variables) => {
      // Invalidate and refetch messages for this chat
      queryClient.invalidateQueries(
        invalidateQueries.pingMessages(variables.chat)
      );

      // Invalidate chat list to update last message and unread counts
      queryClient.invalidateQueries(invalidateQueries.pingChats());
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.previousMessages) {
        queryClient.setQueryData(
          queryKeys.ping.messages.list(variables.chat),
          context.previousMessages
        );
      }
      console.error("Failed to send message:", error);
    },
  });
};

// Mark messages as read mutation - matches Flutter implementation
export const useMarkMessagesAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      messageId,
      chatId,
    }: {
      messageId: string;
      chatId: string;
    }) => pingAPI.messages.markAsRead(messageId),
    onMutate: async ({ messageId, chatId }) => {
      // Cancel any outgoing refetches to avoid conflicts
      await queryClient.cancelQueries({ queryKey: queryKeys.ping.chats.all() });

      // Get previous data for rollback
      const previousChats = queryClient.getQueryData(
        queryKeys.ping.chats.all()
      );

      // Optimistically update chat list to set unread count to 0 and is_unread to false for the specific chat
      queryClient.setQueryData(
        queryKeys.ping.chats.all(),
        (oldData: PaginatedResponse<SupportChat> | undefined) => {
          if (!oldData?.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((chat: SupportChat) => {
              // Find the specific chat and set unread count to 0 and is_unread to false
              if (chat.id === chatId) {
                return {
                  ...chat,
                  unread_count: 0,
                  is_unread: false, // Mark as read when messages are marked as read
                };
              }
              return chat;
            }),
          };
        }
      );

      return { previousChats };
    },
    onSuccess: (response, { messageId, chatId }) => {
      // IMMEDIATELY refetch chats to ensure server sync
      queryClient.refetchQueries({ queryKey: queryKeys.ping.chats.all() });

      // Invalidate messages to update read status
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.messages.all(),
      });

      // Also invalidate the specific chat detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.chats.detail(chatId),
      });
    },
    onError: (error, { messageId, chatId }, context) => {
      console.error(
        `Failed to mark message ${messageId} as read for chat ${chatId}:`,
        error
      );
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData(
          queryKeys.ping.chats.all(),
          context.previousChats
        );
      }
    },
  });
};

export const useMarkPingMessageAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => pingAPI.messages.markAsRead(messageId),
    onSuccess: (response, messageId) => {
      // Invalidate messages to update read status
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.messages.all(),
      });

      // Invalidate chats to update unread counts
      queryClient.invalidateQueries(invalidateQueries.pingChats());
    },
    onError: (error) => {
      console.error("Failed to mark message as read:", error);
    },
  });
};

// Helper mutations using pingHelpers
export const useSendTextMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      text,
      isNote = false,
    }: {
      chatId: string;
      text: string;
      isNote?: boolean;
    }) => pingHelpers.sendTextMessage(chatId, text, isNote),
    onMutate: async (variables) => {
      const { chatId, text, isNote } = variables;

      // Cancel ALL outgoing refetches for this chat's infinite queries (all pageSizes)
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data for rollback (in case of multiple pageSizes)
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      // Determine if current user is a patient or care manager
      const { useAuthStore } = await import("@/stores/authStore");
      const { isPatient } = useAuthStore.getState();
      const isCurrentUserPatient = isPatient();

      // Create optimistic message
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        user_sender: null,
        care_manager_sender: null,
        message_type: "text" as const,
        text_content: text,
        media_file: null,
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_user: false,
        is_read_by_care_manager: false,
        is_from_user: isCurrentUserPatient,
        is_note: isNote,
        sender_name: isNote ? "You (Note)" : "You",
        user_type: "sending...",
      };

      // Optimistically update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch (not just invalidate) to ensure latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.messages.infinite(variables.chatId),
        exact: false,
      });

      // Refetch chat list
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.chats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send text message:", error);
    },
  });
};

export const useSendImageMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      imageFile,
      textContent,
      isNote = false,
    }: {
      chatId: string;
      imageFile: File;
      textContent?: string;
      isNote?: boolean;
    }) => {
      return pingAPI.messages.create({
        chat: chatId,
        message_type: "image",
        text_content: textContent,
        media_file: imageFile,
        is_note: isNote,
      });
    },
    onMutate: async (variables) => {
      const { chatId, imageFile, textContent, isNote } = variables;

      // Cancel ALL outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      const { useAuthStore } = await import("@/stores/authStore");
      const { isPatient } = useAuthStore.getState();
      const isCurrentUserPatient = isPatient();

      // Create optimistic message
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        user_sender: null,
        care_manager_sender: null,
        message_type: "image" as const,
        text_content: textContent || null,
        media_file: URL.createObjectURL(imageFile),
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_user: false,
        is_read_by_care_manager: false,
        is_from_user: isCurrentUserPatient,
        is_note: isNote,
        sender_name: isNote ? "You (Note)" : "You",
        user_type: "sending...",
      };

      // Update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch to get latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.messages.infinite(variables.chatId),
        exact: false,
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.chats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send image message:", error);
    },
  });
};

export const useSendDocumentMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      chatId,
      documentFile,
      textContent,
      isNote = false,
    }: {
      chatId: string;
      documentFile: File;
      textContent?: string;
      isNote?: boolean;
    }) => {
      return pingAPI.messages.create({
        chat: chatId,
        message_type: "document",
        text_content: textContent,
        media_file: documentFile,
        is_note: isNote,
      });
    },
    onMutate: async (variables) => {
      const { chatId, documentFile, textContent, isNote } = variables;

      // Cancel ALL outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      // Snapshot ALL infinite query data
      const previousQueries = queryClient.getQueriesData({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      const { useAuthStore } = await import("@/stores/authStore");
      const { isPatient } = useAuthStore.getState();
      const isCurrentUserPatient = isPatient();

      // Create optimistic message
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        chat: chatId,
        user_sender: null,
        care_manager_sender: null,
        message_type: "document" as const,
        text_content: textContent || null,
        media_file: URL.createObjectURL(documentFile),
        media_thumbnail: null,
        created_at: new Date().toISOString(),
        is_read_by_user: false,
        is_read_by_care_manager: false,
        is_from_user: isCurrentUserPatient,
        is_note: isNote,
        sender_name: isNote ? "You (Note)" : "You",
        user_type: "sending...",
      };

      // Update ALL infinite query variants
      const queries = queryClient.getQueryCache().findAll({
        queryKey: queryKeys.ping.messages.infinite(chatId),
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old) return old;

          const newPages = [...old.pages];
          if (newPages.length > 0) {
            newPages[0] = {
              ...newPages[0],
              results: [optimisticMessage, ...(newPages[0].results || [])],
              count: (newPages[0].count || 0) + 1,
            };
          } else {
            newPages.push({
              count: 1,
              next: null,
              previous: null,
              results: [optimisticMessage],
            });
          }

          return {
            ...old,
            pages: newPages,
            pageParams: old.pageParams,
          };
        });
      });

      return { previousQueries };
    },
    onSuccess: async (message, variables) => {
      // IMMEDIATELY refetch to get latest server data
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.messages.infinite(variables.chatId),
        exact: false,
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.ping.chats.all(),
      });
    },
    onError: (error, variables, context) => {
      // Rollback ALL optimistic updates
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to send document message:", error);
    },
  });
};

// Chat management mutations
export const useUpdateChatStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: {
      chatId: string;
      status: "open" | "closed" | "resolved" | "assigned";
    }) => {
      if (!data.chatId || !data.status) {
        return Promise.reject(new Error("Invalid chat ID or status"));
      }
      return pingAPI.chats.updateStatus(data.chatId, { status: data.status });
    },
    onSuccess: (chat: SupportChat, variables) => {
      // Update chat detail cache directly with the returned chat
      queryClient.setQueryData(
        queryKeys.ping.chats.detail(variables.chatId),
        chat
      );

      // Invalidate chat list to show status changes
      queryClient.invalidateQueries(invalidateQueries.pingChats());

      toast({
        title: "Success",
        description: `Chat ${variables.status === "closed" ? "closed" : "updated"} successfully`,
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update chat status";
      console.error("Failed to update chat status:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useTransferChat = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: {
      fromChatId: string;
      toCareManagerId: string;
      reason?: string;
    }) => {
      if (!data.fromChatId || !data.toCareManagerId) {
        return Promise.reject(new Error("Invalid chat ID or care manager ID"));
      }
      return pingAPI.chats.reassign(data.fromChatId, data.toCareManagerId);
    },
    onSuccess: (chat: SupportChat, variables) => {
      // Update chat detail cache directly with the returned chat
      queryClient.setQueryData(
        queryKeys.ping.chats.detail(variables.fromChatId),
        chat
      );

      // Invalidate chat list
      queryClient.invalidateQueries(invalidateQueries.pingChats());

      toast({
        title: "Success",
        description: "Chat transferred successfully",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to transfer chat";
      console.error("Failed to transfer chat:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Reassign chat mutation - matches Flutter implementation exactly
export const useReassignChat = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: { chatId: string; careManagerId: string }) => {
      // Validate inputs before making API call
      if (!data.chatId || !data.careManagerId) {
        return Promise.reject(new Error("Invalid chat ID or care manager ID"));
      }
      return pingAPI.chats.reassign(data.chatId, data.careManagerId);
    },
    onSuccess: (chat: SupportChat, variables) => {
      // Clear current chat selection (matches Flutter)
      const { clearSelectedChat } = usePingStore.getState();
      clearSelectedChat();

      // Clear messages cache for this chat. ConversationView uses the infinite
      // query, while a few older paths still use the list query.
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.messages.list(variables.chatId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.messages.infinite(variables.chatId),
      });

      // Clear chat detail cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.chats.detail(variables.chatId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ping.chats.reassignmentTargets(variables.chatId),
      });

      // Invalidate and refetch all chats (matches Flutter getAllChats())
      queryClient.invalidateQueries(invalidateQueries.pingChats());

      toast({
        title: "Success",
        description: "Chat reassigned successfully",
      });
    },
    onError: (error: any) => {
      const isPermissionError =
        error?.status === 403 || error?.response?.status === 403;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reassign chat";
      console.error("Failed to reassign chat:", error);
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow this chat assignment or transfer."
          : errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Star chat mutation - marks a chat as starred and refreshes lists
export const useStarChat = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => {
      if (!chatId) {
        return Promise.reject(new Error("Invalid chat ID"));
      }
      return pingAPI.chats.star(chatId);
    },
    onMutate: async ({ chatId }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.chats.detail(chatId),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.ping.chats.all(),
        exact: false,
      });

      const previousDetail = queryClient.getQueryData<SupportChatDetail>(
        queryKeys.ping.chats.detail(chatId)
      );
      const previousListQueries = queryClient.getQueriesData<
        PaginatedResponse<SupportChat>
      >({
        queryKey: queryKeys.ping.chats.all(),
        exact: false,
      });

      const currentIsStarred =
        previousDetail?.is_starred ??
        previousListQueries
          .filter(([queryKey]) => queryKey[2] === "list")
          .flatMap(([, data]) => data?.results ?? [])
          .find((cachedChat) => cachedChat.id === chatId)?.is_starred ??
        false;

      const nextIsStarred = !currentIsStarred;

      queryClient.setQueryData(
        queryKeys.ping.chats.detail(chatId),
        (existingChat: SupportChatDetail | undefined) =>
          existingChat
            ? { ...existingChat, is_starred: nextIsStarred }
            : existingChat
      );

      updateSupportChatInCachedLists(queryClient, chatId, (chat) => ({
        ...chat,
        is_starred: nextIsStarred,
      }));

      return { previousDetail, previousListQueries, nextIsStarred };
    },
    onSuccess: (chat: SupportChat | undefined, variables, context) => {
      const serverChat = chat ?? null;

      // Merge with existing detail so partial mutation responses don't blank the conversation pane.
      queryClient.setQueryData(
        queryKeys.ping.chats.detail(variables.chatId),
        (existingChat: SupportChatDetail | undefined) =>
          existingChat
            ? {
                ...existingChat,
                ...(serverChat ?? {}),
                is_starred:
                  context?.nextIsStarred ??
                  serverChat?.is_starred ??
                  existingChat.is_starred,
              }
            : serverChat
              ? {
                  ...serverChat,
                  is_starred: context?.nextIsStarred ?? serverChat.is_starred,
                }
              : existingChat
      );

      // Keep cached chat lists in sync without forcing the active conversation to refetch.
      updateSupportChatInCachedLists(
        queryClient,
        variables.chatId,
        (cached) => ({
          ...cached,
          ...(serverChat ?? {}),
          is_starred:
            context?.nextIsStarred ??
            serverChat?.is_starred ??
            cached.is_starred,
        })
      );

      toast({
        title: "Success",
        description:
          context?.nextIsStarred === false ? "Chat unstarred" : "Chat starred",
      });
    },
    onError: (error: any, variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.ping.chats.detail(variables.chatId),
          context.previousDetail
        );
      }

      context?.previousListQueries
        ?.filter(([queryKey]) => queryKey[2] === "list")
        .forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to star chat";
      console.error("Failed to star chat:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

// Toggle chat read/unread mutation - toggles between read/unread states
export const useMarkChatAsUnread = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => {
      if (!chatId) {
        return Promise.reject(new Error("Invalid chat ID"));
      }
      return pingAPI.chats.markAsUnread(chatId);
    },
    onMutate: async ({ chatId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.ping.chats.all() });

      // Get previous data for rollback
      const previousChats = queryClient.getQueryData(
        queryKeys.ping.chats.all()
      );

      // Optimistically toggle is_unread state
      queryClient.setQueryData(
        queryKeys.ping.chats.all(),
        (oldData: PaginatedResponse<SupportChat> | undefined) => {
          if (!oldData?.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((chat: SupportChat) => {
              if (chat.id === chatId) {
                return {
                  ...chat,
                  is_unread: !chat.is_unread, // Toggle the state
                };
              }
              return chat;
            }),
          };
        }
      );

      return { previousChats };
    },
    onSuccess: (chat: SupportChat, variables) => {
      // Update chat detail cache directly
      queryClient.setQueryData(
        queryKeys.ping.chats.detail(variables.chatId),
        chat
      );

      // Refetch chat list to show/hide unread indicator
      queryClient.refetchQueries({ queryKey: queryKeys.ping.chats.all() });

      // Show appropriate message based on the new state
      const message = chat.is_unread
        ? "Chat marked as unread"
        : "Chat marked as read";
      toast({ title: "Success", description: message });
    },
    onError: (error: any, variables, context) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update chat status";
      console.error("Failed to toggle chat read/unread status:", error);
      // Rollback the optimistic update
      if (context?.previousChats) {
        queryClient.setQueryData(
          queryKeys.ping.chats.all(),
          context.previousChats
        );
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};
